import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import {PorQue} from '@/components/ui/por-que'
import {formatDateOnly} from '@/lib/risk'
import type {CondicionCatalogo, Emparejamientos} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'

const ESTADO_TONE: Record<CondicionCatalogo['estado_norma'], 'green' | 'amber' | 'neutral'> = {
  verificada: 'green',
  articulo_por_confirmar: 'amber',
  sin_norma_verificada: 'neutral',
  relacion_normas_por_verificar: 'amber',
}

/** Nunca imprime un artículo mientras el estado no sea `verificada` (§2 de la spec).
 * No se pliega: es la cita de la norma (T8). */
export function NormaLinea({fila}: {fila: CondicionCatalogo}) {
  const {t} = useTranslation()
  return (
    // `clave`, no `condicion`: más de una fila vigente puede compartir condición (D5).
    <span className="text-xs text-muted-foreground" data-testid={`norma-${fila.clave}`}>
      {t(`catalogoKairos.norma.${fila.estado_norma}`, {norma: fila.norma_nombre, otra: fila.relacion_norma})}
    </span>
  )
}

/** `semilla_minima` lo dice para que nadie lea un vacío como el catálogo completo. */
export function CatalogoKairos({catalogo}: {catalogo: Emparejamientos['catalogo']}) {
  const {t} = useTranslation()
  const [abierto, setAbierto] = useState(false)
  return (
    <section className="surface rounded-lg p-4" data-testid="catalogo-kairos">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 font-heading text-base tracking-tight">
          {t('catalogoKairos.title')}
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('catalogoKairos.title')})}
            testId="catalogo-kairos-ayuda">
            <p>{t('catalogoKairos.intro')}</p>
          </HelpIcon>
        </h2>
        <Button size="sm" variant="outline" onClick={() => setAbierto((previo) => !previo)}>
          {t(abierto ? 'catalogoKairos.ocultarNormas' : 'catalogoKairos.verNormas')}
        </Button>
      </div>
      <p
        className="mt-1 font-mono text-xs tracking-wider text-muted-foreground uppercase"
        data-testid="catalogo-versiones">
        {/* El relleno conserva su propia versión: es normalización, no catálogo. */}
        {t('catalogoKairos.versiones', {
          fecha: formatDateOnly(catalogo.fecha),
          relleno: catalogo.relleno_version,
        })}
      </p>
      {catalogo.semilla_minima && (
        <p className="notice-amber mt-2 rounded-lg px-3 py-2 text-sm" data-testid="catalogo-semilla">
          {t('catalogoKairos.semilla')}
        </p>
      )}
      {/* Condicionado al estado por fila: se corrige solo cuando Inci verifique la primera norma. */}
      {catalogo.condiciones.every((fila) => fila.estado_norma !== 'verificada') && (
        <div className="mt-2">
          <PorQue
            label={t('catalogoKairos.sinFirmanteLabel')}
            testId="catalogo-sin-firmante"
            contenidoTestId="catalogo-sin-firmante-contenido">
            <p>{t('catalogoKairos.sinFirmante')}</p>
          </PorQue>
        </div>
      )}
      {abierto && (
        <ul className="mt-3 space-y-2" data-testid="catalogo-condiciones">
          {catalogo.condiciones.map((fila) => (
            <li key={fila.clave} className="surface-divider border-t pt-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground">{fila.nombre}</span>
                <Tag tone={ESTADO_TONE[fila.estado_norma]}>{t(`catalogoKairos.estado.${fila.estado_norma}`)}</Tag>
                {fila.horas !== null && <Tag>{t('catalogoKairos.horas', {horas: fila.horas})}</Tag>}
              </div>
              <NormaLinea fila={fila} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
